import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { MeResponse } from '@dimmi/shared';

import { ApiError, api } from '@/lib/api';

export const useAccountStore = defineStore('account', () => {
  const account = ref<MeResponse | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  // Set for the auth-state transition right after `register()` so the app can
  // route a brand-new account to Dimmitri onboarding instead of the dashboard.
  const justRegistered = ref(false);

  async function refresh() {
    loading.value = true;
    error.value = null;
    try {
      account.value = await api.get<MeResponse>('/api/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        account.value = null;
      } else if (err instanceof Error) {
        error.value = err.message;
      } else {
        error.value = 'Unknown error';
      }
    } finally {
      loading.value = false;
    }
  }

  async function login(email: string, password: string) {
    account.value = await api.post<MeResponse>('/api/auth/login', { email, password });
  }

  async function register(payload: { email: string; password: string; displayName: string }) {
    const created = await api.post<MeResponse>('/api/auth/register', payload);
    justRegistered.value = true;
    account.value = created;
  }

  async function logout() {
    await api.post<void>('/api/auth/logout');
    account.value = null;
    justRegistered.value = false;
  }

  async function updateProfile(displayName: string) {
    account.value = await api.patch<MeResponse>('/api/account', { displayName });
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    await api.post<void>('/api/account/password', { currentPassword, newPassword });
  }

  // « Mot de passe oublié » — step 1: ask for a reset email. Resolves the same
  // way whether or not the email matched an account (no enumeration).
  async function requestPasswordReset(email: string) {
    await api.post<void>('/api/auth/forgot-password', { email });
  }

  // Tells whether a reset link is still usable, before showing the form.
  async function validateResetToken(token: string): Promise<boolean> {
    const res = await api.get<{ valid: boolean }>(
      `/api/auth/reset-password/${encodeURIComponent(token)}`,
    );
    return res.valid;
  }

  // Step 2: set the new password. The server opens a fresh session, so the
  // account is logged in on success.
  async function completePasswordReset(token: string, password: string) {
    account.value = await api.post<MeResponse>('/api/auth/reset-password', {
      token,
      password,
    });
  }

  return {
    account,
    loading,
    error,
    justRegistered,
    refresh,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    requestPasswordReset,
    validateResetToken,
    completePasswordReset,
  };
});
